import { forwardRef, type KeyboardEvent } from 'react';
import { ArrowUp, Square } from 'lucide-react';
import { cn } from '../ui';
import { AiSpark } from './tool-cards/spark';

/**
 * The full-page Agente composer bar — one elegant input line matching the demo
 * reel: an AI spark on the left, a borderless input, a mono "AI" pill, and a
 * circular send button. The drawer keeps its own inline input; this is the page
 * surface's composer. Focus-within warms the border and lifts a soft shadow.
 *
 * The input is 16px (`text-base`) so iOS doesn't zoom on focus (chokehold #8).
 */
export const Composer = forwardRef<
  HTMLInputElement,
  {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    /** True while a turn is in flight — swaps send for Stop, mirrors the drawer's guard. */
    thinking: boolean;
    /** Abort the in-flight turn — the Stop control shown while `thinking`. */
    onStop?: (() => void) | undefined;
    onFocus?: (() => void) | undefined;
    onBlur?: (() => void) | undefined;
    /** Page-owned key handler (palette navigation + Enter-to-send). */
    onKeyDown?: ((e: KeyboardEvent<HTMLInputElement>) => void) | undefined;
    className?: string | undefined;
  }
>(function Composer(
  { value, onChange, onSubmit, thinking, onStop, onFocus, onBlur, onKeyDown, className },
  ref
) {
  const canSend = !thinking && value.trim().length > 0;
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-2xl border border-line bg-panel px-4 py-3 shadow-sm transition-all focus-within:border-primary/40 focus-within:shadow-[0_14px_40px_-16px_rgba(138,46,60,.28)]',
        className
      )}
    >
      <AiSpark className="h-5 w-5 shrink-0" />
      <input
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (onKeyDown) {
            onKeyDown(e);
            return;
          }
          if (e.key === 'Enter') onSubmit();
        }}
        placeholder="Pregunta o pide una acción a Cumpli AI…"
        maxLength={2000}
        aria-label="Mensaje para Cumpli AI"
        className="min-w-0 flex-1 bg-transparent text-base text-ink outline-none placeholder:text-ink-faint"
      />
      <span className="hidden shrink-0 rounded-md border border-primary/20 bg-primary/5 px-1.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-primary xs:inline-block">
        AI
      </span>
      {thinking ? (
        <button
          type="button"
          onClick={onStop}
          aria-label="Detener"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary text-white shadow-[0_6px_14px_-6px_rgba(138,46,60,.6)] transition-colors hover:bg-primary-dark"
        >
          <Square className="h-3.5 w-3.5 fill-current" strokeWidth={2.5} />
        </button>
      ) : (
        <button
          onClick={onSubmit}
          disabled={!canSend}
          aria-label="Enviar"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary text-white shadow-[0_6px_14px_-6px_rgba(138,46,60,.6)] transition-opacity hover:bg-primary-dark disabled:opacity-40"
        >
          <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
});
